import React, { useState, useMemo, useEffect } from 'react'
import PrimaryModal from '../../UI/Modal/PrimaryModal'
import Loader from '../../UI/Loader/Loader';
import { toast } from 'react-toastify';
import { useUserContext } from '../../../contexts/UserContext';
import { getNewWorkspaceData } from '../../../utils/APIs';
import { useContentfulContext } from '../../../contexts/ContentfulContext';
import AgGridWrapper from '../../UI/AgGrid';

const AdobeTargetWorkspaceModal = ({ handleClose }) => {
  const [loading, setLoading] = useState(false);
  const [workspaceData, setWorkspaceData] = useState([]);
  const [selectedItem, setSelectedItem] = useState();
  const [gridApi, setGridApi] = useState(null);

  const { userData, updateDefaultWorkspace } = useUserContext();
  const { content } = useContentfulContext();

  useEffect(() => {
    if(userData?.defaultAdobeIO)
      getAllWorkspaceData();
  }, [userData]);

  const getAllWorkspaceData = async () => {
    try {
      setLoading(true);
      const result = await getNewWorkspaceData(
        userData.token,
        userData.selectedOrganization.org_token,
        userData?.defaultAdobeIO.tenant_name
      );
      if(result?.data?.length){
        const updated = result.data.map((x) => ({
          ...x,
          default: userData?.defaultWorkspace?._id == x._id ? true : false
        }))
        setWorkspaceData(updated);
        // Set the default selected item
        const defaultItem = updated.find((item) => item.default);
        if (defaultItem) setSelectedItem(defaultItem);
      }
    } catch (error) {
      toast.error(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
    } finally {
      setLoading(false);
    }
  };

  const handleRowSelected = (event) => {
    const selectedRow = event.data;
    if (selectedItem?._id !== selectedRow._id) {
        setSelectedItem(selectedRow);
        toast.success(content.GLOBAL_SELECTOR_CONTENT.workspace.success_msg);
        updateDefaultWorkspace(selectedRow);
    }
};

const CheckboxRenderer = (params) => {
    const isChecked = selectedItem?._id === params.data._id;
    return (
        <input
            type="checkbox"
            checked={isChecked}
            onChange={() => handleRowSelected(params)}
            className="cursor-pointer rounded-lg w-4 h-4"
        />
    );
};

  const colDefs = [
    {
      headerName: "",
      field: "checkbox",
      cellRenderer: CheckboxRenderer,
      suppressMovable: true,
    },
    {
      headerName: content.GLOBAL_SELECTOR_CONTENT.workspace.table.workspace_id,
      field: "workspace_id",
      cellRenderer: ({ data }) => (
        <button className="text-center block cursor-pointer text-primary">
          {data.workspace_id}
        </button>
      ),
      filter: true,
      sortable: true,
      suppressMovable: true,
      resizable: false,
    },
    {
      headerName: content.GLOBAL_SELECTOR_CONTENT.workspace.table.workspace_name,
      field: "workspace_name",
      filter: true,
      sortable: true,
      suppressMovable: true,
      resizable: false,
    },
  ];

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const autoSizeStrategy = useMemo(() => {
    return {
      type: "fitGridWidth",
      defaultMinWidth: 60,
      columnLimits: [
        {
          colId: "checkbox",
          maxWidth: 80,
        },
        {
          colId: "workspace_id",
          minWidth: 300,
        },
        {
          colId: "workspace_name",
          minWidth: 350,
        },
      ],
    };
  }, []);

  const onFirstDataRendered = (params) => {
    if (workspaceData.length > 0) {
        const defaultRow = workspaceData.find((row) => row.default);
        if (defaultRow) {
            setSelectedItem(defaultRow);
        }
    }
};

  const onFilterTextBoxChanged = (event) => {
    const filterValue = event.target.value.toLowerCase();
    gridApi.setFilterModel({
      workspace_name: { type: "contains", filter: filterValue },
    });
  };

  return (
    <PrimaryModal
      title={content.GLOBAL_SELECTOR_CONTENT.workspace.title}
      hideModal={() => handleClose()}
      widthClass="max-w-5xl"
    >
      {loading && <Loader />}
      <div className="p-7 pt-1 space-y-6">
        <p>{content.GLOBAL_SELECTOR_CONTENT.workspace.description}</p>
        <div className="flex justify-between items-center">
          <input
            type="text"
            className="block px-2.5 py-2.5 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none max-w-64"
            placeholder="Search by name"
            onChange={onFilterTextBoxChanged}
          />
        </div>
        <div
          className={"ag-theme-quartz"}
          style={{ height: "100%", width: "100%" }}
        >
          <AgGridWrapper
            tableId="global-selector-adobe-target-workspace-table"
            rowData={workspaceData}
            columnDefs={colDefs}
            autoSizeStrategy={autoSizeStrategy}
            onGridReady={onGridReady}
            onFirstDataRendered={onFirstDataRendered}
            rowSelection="single"
            suppressRowClickSelection
            onRowSelected={handleRowSelected}
          />
        </div>
      </div>
    </PrimaryModal>
  );
};

export default AdobeTargetWorkspaceModal;
